import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance with HTTP-only cookie support
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor (cookies are sent automatically with withCredentials)
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest.url || '';

    // Check if this is an auth-related request (login, logout, refresh, etc)
    const isAuthRequest = requestUrl.includes('/auth/login') || 
                         requestUrl.includes('/auth/logout') || 
                         requestUrl.includes('/auth/refresh') ||
                         requestUrl.includes('/auth/change-password');

    // Don't retry the refresh endpoint itself to prevent infinite loop
    if (requestUrl.includes('/auth/refresh')) {
      console.error('Refresh token attempt failed:', error.response?.status);
      // If refresh fails, user not authenticated - redirect to login
      if (error.response?.status === 400 || error.response?.status === 401) {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }

    // Never attempt token refresh for any auth-related request
    if (isAuthRequest && error.response?.status === 401) {
      console.warn('Auth request failed with 401:', requestUrl);
      return Promise.reject(error);
    }

    // If error is 401 for non-auth requests, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('Access token expired, attempting refresh...');
      originalRequest._retry = true;

      try {
        // Refresh token endpoint will set new token in HTTP-only cookie
        const response = await api.post('/api/auth/refresh');
        console.log('Token refresh successful');
        // Token is now in cookie, retry original request
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Refresh failed, logout user by redirecting to login
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  logout: () => api.post('/api/auth/logout'),
  getProfile: () => api.get('/api/auth/profile'),
  changePassword: (data) => api.post('/api/auth/change-password', data),
  verify2FA: (data) => api.post('/api/auth/2fa/verify', data),
  setup2FA: () => api.post('/api/auth/2fa/setup'),
  enable2FA: (token) => api.post('/api/auth/2fa/enable', { token }),
  disable2FA: (password) => api.post('/api/auth/2fa/disable', { password }),
};

// Content API
export const contentAPI = {
  getAll: (params) => api.get('/api/content', { params }),
  getById: (id) => api.get(`/api/content/${id}`),
  upload: (formData) => api.post('/api/content/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  update: (id, data) => api.put(`/api/content/${id}`, data),
  delete: (id) => api.delete(`/api/content/${id}`),
  getMyContent: () => api.get('/api/content'),
  // Playlist operations
  getPlaylist: () => api.get('/api/content/playlist/items'),
  addToPlaylist: (id) => api.post(`/api/content/${id}/playlist/add`),
  removeFromPlaylist: (id) => api.delete(`/api/content/${id}/playlist/remove`),
  reorderPlaylist: (items) => api.post('/api/content/playlist/reorder', { items }),
};

// Activity API
export const activityAPI = {
  getMyActivity: (params) => api.get('/api/activity/my', { params }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/user-stats'),
  getMyScreens: () => api.get('/api/dashboard/my-screens'),
};

// Location API (for dropdown)
export const locationAPI = {
  getLocationsDropdown: () => api.get('/api/admin/locations/dropdown'),
};

// Campaign API (Sub User)
export const campaignAPI = {
  getAllCampaigns: (params) => api.get('/api/admin/campaigns', { params }),
  getCampaignById: (id) => api.get(`/api/admin/campaigns/${id}`),
  createCampaign: (data) => api.post('/api/admin/campaigns', data),
  updateCampaign: (id, data) => api.put(`/api/admin/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/api/admin/campaigns/${id}`),
  uploadCreative: (id, formData) => api.post(`/api/admin/campaigns/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadMultipleCreatives: (id, formData) => api.post(`/api/admin/campaigns/${id}/upload-multiple`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteMediaFile: (id, mediaId) => api.delete(`/api/admin/campaigns/${id}/media/${mediaId}`),
  assignSlots: (id, data) => api.post(`/api/admin/campaigns/${id}/assign-slots`, data),
  submitForApproval: (id) => api.post(`/api/admin/campaigns/${id}/submit`),
  getCampaignStats: () => api.get('/api/admin/campaigns/stats/overview'),
  checkSlotAvailability: (data) => api.post('/api/admin/campaigns/check-availability', data),
};

// Screen API (for slot selection)
export const screenAPI = {
  getScreensByLocation: (locationId) => api.get(`/api/admin/screens/location/${locationId}`),
  getScreenSlots: (id) => api.get(`/api/admin/screens/${id}/slots`),
};

export default api;